import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { MenuItemType } from '@/app/types/layout';
import { CommonModule } from '@angular/common';
import { NgIcon } from '@ng-icons/core';
import { NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { scrollToElement } from '@/app/utils/layout-utils';
import { LayoutStoreService } from '@core/services/layout-store.service';
import { OpcionesMenuService } from '@/app/pages/principal/opciones-menu/opciones-menu.service';
import { Menu } from '@core/models/user.model';

@Component({
  selector: 'app-menu',
  imports: [CommonModule, NgIcon, NgbCollapse, RouterLink],
  templateUrl: './app-menu.component.html'
})
export class AppMenuComponent implements OnInit {

  router = inject(Router)
  layout = inject(LayoutStoreService)
  private menuService = inject(OpcionesMenuService)

  @ViewChild('MenuItemWithChildren', { static: true })
  menuItemWithChildren!: TemplateRef<{ item: MenuItemType }>;

  @ViewChild('MenuItem', { static: true })
  menuItem!: TemplateRef<{ item: MenuItemType }>;

  menuItems: MenuItemType[] = [];

  ngOnInit(): void {
    this.loadUserMenu();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.expandActivePaths(this.menuItems);
        setTimeout(() => this.scrollToActiveLink(), 50);
      });
  }

  private loadUserMenu(): void {
    this.menuService.getUserMenu().subscribe(menus => {
      this.menuItems = this.transformMenus(menus);
      this.expandActivePaths(this.menuItems);
      setTimeout(() => this.scrollToActiveLink(), 100);
    });
  }

  private transformMenus(menus: Menu[]): MenuItemType[] {
    return menus.map(menu => {
      const item: MenuItemType = {
        label: menu.nombre ?? '',
        icon: menu.icono,
        color: menu.color,
        url: menu.ruta ?? undefined,
      };
      if (menu.children && menu.children.length > 0) {
        item.children = this.transformMenus(menu.children);
        item.isCollapsed = true;
      }
      return item;
    });
  }

  hasSubMenu(item: MenuItemType): boolean {
    return !!item.children;
  }

  expandActivePaths(items: MenuItemType[]) {
    for (const item of items) {
      if (this.hasSubMenu(item)) {
        item.isCollapsed = !this.isChildActive(item);
        this.expandActivePaths(item.children || []);
      }
    }
  }

  isChildActive(item: MenuItemType): boolean {
    if (item.url && this.router.url.startsWith(item.url)) return true;
    if (item.children) {
      return item.children.some((child: MenuItemType) => this.isChildActive(child));
    }
    return false;
  }

  isActive(item: MenuItemType): boolean {
    if (!item.url) return false;
    return this.router.url.startsWith(item.url);
  }

  scrollToActiveLink(): void {
    const activeItem = document.querySelector('[data-active-link="true"]') as HTMLElement;
    const scrollContainer = document.querySelector("#sidenav .simplebar-content-wrapper") as HTMLElement;

    if (activeItem && scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();

      const offset = itemRect.top - containerRect.top - window.innerHeight * 0.4;

      scrollToElement(scrollContainer, scrollContainer.scrollTop + offset, 500);
    }
  }

  hideBackDrop(url: string): void {
    if (this.layout.sidenavSize === 'offcanvas' && this.router.url !== url) {
      this.layout.hideBackdrop();
    }
  }

  navigateOrToggle(item: MenuItemType) {
    item.isCollapsed = !item.isCollapsed;
    if (item.url) {
      this.router.navigate([item.url]);
    }
    this.hideBackDrop(item.url || '');
  }
}
